// Updated API service to use the correct endpoints from the documentation

import { backendMapper, BackendApiResponse } from './mapper';

// API Service constants based on documentation
export const API_ENDPOINTS = {
  AUTH: {
    LOGIN: '/auth/login',
    REFRESH: '/auth/refresh',
    ME: '/auth/me',
    VERIFY: '/auth/verify',
    LOGOUT: '/auth/logout',
  },
  USERS: {
    BASE: '/users',
    REGISTER: '/users/register',
    REGISTER_GOOGLE: '/users/register/google',
    TYPES: '/users/types',
    DEVICES: (userId: number) => `/users/${userId}/devices`,
    REGISTER_DEVICE: (userId: number) => `/users/${userId}/devices/register`,
    SAVED_EVENTS: (userId: number) => `/users/${userId}/saved-events`,
    SAVE_EVENT: (userId: number, eventId: number) => `/users/${userId}/saved-events/${eventId}`,
    DELETE_SAVED_EVENT: (userId: number, eventId: number) => `/users/${userId}/saved-events/${eventId}`,
    MOBILE_PROFILE: (id: string | number) => `/users/mo/${id}`,
    BY_EMAIL: (email: string) => `/users/${email}/email`,
    UPDATE: (id: string | number) => `/users/${id}`,
    BAN: '/users/ban',
    UNBAN: '/users/unban',
  },
  EVENTS: {
    MOBILE_BASE: '/mobile/events',
    BO_BASE: '/bo/events',
    PUBLIC_BASE: '/events',
    MOBILE_DETAIL: (id: string | number) => `/mobile/events/${id}`,
    BO_DETAIL: (id: string | number) => `/bo/events/${id}`,
    PUBLIC_DETAIL: (id: string | number) => `/events/detail/${id}`,
    MOBILE_USERS: (id: string | number) => `/mobile/events/${id}/users`,
    MOBILE_MESSAGES: (id: string | number) => `/mobile/events/${id}/messages`,
    BO_USERS: (id: string | number) => `/bo/events/${id}/users`,
    ADD_USERS: '/bo/events/addUsers',
    REMOVE_USERS: (id: string | number) => `/bo/events/${id}/users`,
    MARK_PAID: (id: string | number) => `/bo/events/${id}/users/pay`,
    MARK_UNPAID: (id: string | number) => `/bo/events/${id}/users/unpaid`,
    IMAGES: (id: string | number) => `/bo/events/${id}/images`,
    IMAGE_BY_ID: (id: string | number, imageId: number) => `/bo/events/${id}/images/${imageId}`,
    TOGGLE_STATE: (id: string | number) => `/bo/events/${id}/state`,
    ACTIVATE: (id: string | number) => `/bo/events/${id}/state/activate`,
    CANCELLATIONS: (id: string | number) => `/bo/events/${id}/cancellations`,
    UPDATE_HOST: '/bo/events/host',
    RECURRENT: {
      BASE: '/bo/events/recurrent',
      BY_ID: (id: string | number) => `/bo/events/recurrent/${id}`,
    },
    MOBILE_JOIN: '/mobile/events/addUser',
    MOBILE_LEAVE: (id: string | number) => `/mobile/events/${id}/user`,
    MOBILE_CANCEL_REGISTRATION: '/mobile/events/cancel-registration',
    MOBILE_USER_EVENTS: (userId: string | number) => `/mobile/events/users/${userId}/events`,
  },
  NOTIFICATIONS: {
    PUSH_DEEPLINK: '/notifications/push/send/deeplink',
    BULK: '/notifications/bulk',
    PUSH_TOKEN: (email: string) => `/notifications/${email}/pushToken`,
    USER_NOTIFICATIONS: (userId: string | number) => `/notifications/${userId}/notifications`,
    MARK_READ: (notificationId: string | number) => `/notifications/${notificationId}/read`,
  },
  MESSAGING: {
    MOBILE_DEBUG: '/mobile/messaging/debug',
    MOBILE_CONFIG: '/mobile/messaging/config',
    MOBILE_BOOTSTRAP: '/mobile/messaging/bootstrap',
    MOBILE_FORCE_JOIN: '/mobile/messaging/force-join',
    MOBILE_ENSURE_GROUP: '/mobile/messaging/ensure-group',
    BO_LOGIN: '/bo/messaging/login',
    BO_GROUPS: '/bo/messaging/groups',
    BO_GROUP_MEMBERS: (groupId: string) => `/bo/messaging/groups/${groupId}/members`,
    BO_GROUP_MESSAGES: (groupId: string) => `/bo/messaging/groups/${groupId}/messages`,
    BO_ACTION: '/bo/messaging/action',
    BO_SEND_MESSAGE: '/bo/messaging/send-message',
    BO_AUTH_TOKEN: (uid: string) => `/bo/messaging/auth-token/${uid}`,
  },
  PAYMENT: {
    CREATE_PREFERENCE: '/payment/create-preference',
    HANDLE_SUCCESS: '/payment/handle-success',
    HANDLE_FAILURE: '/payment/handle-failure',
    CREATE_REFUND: '/payment/create-refund',
    WEBHOOK: '/payment/webhook',
  },
  RATINGS: {
    BASE: '/ratings',
    HOST: '/ratings/host',
    BY_USER_EVENT: (userId: string | number, eventId: string | number) => `/ratings/${userId}/${eventId}`,
    EVENTS_TOP: '/ratings/events/top',
    EVENT_STATS: (eventId: string | number) => `/ratings/events/${eventId}/stats`,
    USER_STATS: (userId: string | number) => `/ratings/users/${userId}/stats`,
  },
  REPORTS: {
    USERS_COUNT: '/reports/usersCount',
    CUSTOMERS_UNIQUE: '/reports/customers/unique',
    CUSTOMERS_REPEATING: '/reports/customers/repeating',
    METRICS: '/reports/metrics',
    METRICS_BY_ID: (id: string | number) => `/reports/metrics/${id}`,
    EVENTS_EXPORT: '/bo/reports/events/export',
    // Reportes de auditoria (GTK-745): un endpoint para todos los reportes,
    // el reporte va en el query param `report`.
    AUDIT_CATALOG: '/bo/reports/audit/catalog',
    AUDIT: '/bo/reports/audit',
    AUDIT_EXPORT: '/bo/reports/audit/export',
  },
  BENEFITS: {
    METRICS: '/bo/benefits/metrics',
    METRICS_EXPORT: '/bo/benefits/metrics/export',
  },
  ACTIVITY: {
    ACTIVE_USERS: '/bo/activity/active-users',
    ACTIVE_USERS_EXPORT: '/bo/activity/active-users/export',
  },
  TENANTS: {
    BASE: '/bo/tenants',
    BY_ID: (id: string) => `/bo/tenants/${id}`,
  },
  SYSTEM: {
    HEALTH: '/health',
    READY: '/health/ready',
    LIVE: '/health/live',
    // GTK-898 — avisos del feed; el estado bloqueante va por MAINTENANCE_STATUS.
    MAINTENANCE: '/system/maintenance',
    MAINTENANCE_STATUS: '/system/maintenance/status',
    VERSION: '/system/version',
  },
  UPLOADS: {
    PRESIGNED_URL: '/bo/uploads/presigned-url',
  },
  RULES: {
    FICTIONAL: {
      BASE: '/bo/rules/fictional',
      TOGGLE: (id: number) => `/bo/rules/fictional/${id}/toggle`,
    },
    FEATURED_BADGE: {
      BASE: '/bo/rules/featured-badge',
      CREATE: '/bo/rules/featured-badge/create',
      UPDATE: '/bo/rules/featured-badge/update',
      TOGGLE: '/bo/rules/featured-badge/toggle',
      DELETE: '/bo/rules/featured-badge/delete',
      DELETE_ENTRIES: '/bo/rules/featured-badge/delete-entries',
    },
    EVENT_ADVANCED_CONFIG: {
      BASE: '/bo/rules/event-advanced-config',
      TOGGLE: (id: number) => `/bo/rules/event-advanced-config/${id}/toggle`,
    },
    POINTS: {
      BASE: '/bo/rules/points',
      BY_ID: (id: number) => `/bo/rules/points/${id}`,
      STREAK: '/bo/rules/points/streak',
    },
  },
  POINTS: {
    METRICS: '/bo/points/metrics',
    REWARDS: '/bo/points/rewards',
    REWARD_BY_ID: (id: number) => `/bo/points/rewards/${id}`,
    REDEMPTIONS: '/bo/points/redemptions',
    REDEMPTIONS_EXPORT: '/bo/points/redemptions/export',
    LEDGER: '/bo/points/ledger',
    LEDGER_ADJUSTMENTS: '/bo/points/ledger/adjustments',
    LEDGER_ADJUSTMENT_USERS: '/bo/points/ledger/adjustments/users',
    SETTINGS: '/bo/points/settings',
  },
  EVENT_CANCELLATIONS: {
    REASONS: '/admin/cancellation-reasons',
    REASON_BY_ID: (id: number) => `/admin/cancellation-reasons/${id}`,
    REASONS_REORDER: '/admin/cancellation-reasons/reorder',
    REASONS_RESET: '/admin/cancellation-reasons/reset',
    CANCEL_EVENT: (id: string | number) => `/admin/events/${id}/cancel`,
    EVENT_CANCELLATION: (id: string | number) => `/admin/events/${id}/cancellation`,
    LIST: '/admin/event-cancellations',
    SUMMARY: '/admin/event-cancellations/summary',
    EXPORT: '/admin/event-cancellations/export',
  },
  FEATURE_FLAGS: {
    BASE: '/bo/feature-flags',
    BY_TENANT: (tenantId: string) => `/bo/feature-flags/${tenantId}`,
    UPDATE: (tenantId: string, flagKey: string) =>
      `/bo/feature-flags/${tenantId}/${flagKey}`,
  },
  SHARE_SETTINGS: {
    BASE: '/bo/share-settings',
    BY_ID: (id: string) => `/bo/share-settings/${id}`,
  },
  HOME_SECTIONS: {
    BASE: '/bo/home/sections',
    BY_ID: (id: string) => `/bo/home/sections/${id}`,
  },
  ATTENDANCES: {
    LIST: '/admin/attendance',
    EXPORT: '/admin/attendance/export',
    BY_ID: (id: number) => `/admin/attendance/${id}`,
    USER_HISTORY: (userId: number) => `/admin/attendance/user/${userId}/history`,
    EVENT_CONFIG: (eventId: number) => `/admin/events/${eventId}/attendance-config`,
  },
  BANNERS: {
    BASE: '/bo/banners',
    BY_ID: (id: string) => `/bo/banners/${id}`,
    STATUS: (id: string) => `/bo/banners/${id}/status`,
  },
} as const;

// API error class
export class ApiError extends Error {
  constructor(
    message: string,
    public code?: string,
    public status?: number,
    public details?: any
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// API request utility with error handling and mapping
export const apiRequest = async <T, U = T>(
  request: () => Promise<BackendApiResponse<U>>,
  mapper?: (data: U, mapperInstance: typeof backendMapper) => T,
  options: {
    fallback?: T;
    logErrors?: boolean;
    showErrorToast?: boolean;
  } = { logErrors: true, showErrorToast: false }
): Promise<T> => {
  try {
    const response = await request();
    const mappedResponse = backendMapper.mapApiResponse(response);
    
    if (!mappedResponse.success) {
      throw new ApiError(
        mappedResponse.message || 'Request failed',
        'API_ERROR',
        undefined,
        mappedResponse
      );
    }
    
    if (mappedResponse.data) {
      return mapper ? mapper(mappedResponse.data, backendMapper) : (mappedResponse.data as unknown as T);
    }
    
    throw new ApiError('No data in response', 'NO_DATA');
    
  } catch (error) {
    if (options.logErrors) {
      console.error('API request failed:', error);
    }
    
    if (options.showErrorToast && typeof window !== 'undefined') {
      // You can integrate with your toast system here
      // toast.error(error instanceof Error ? error.message : 'Request failed');
    }
    
    if (options.fallback !== undefined) {
      return options.fallback;
    }
    
    throw error;
  }
};

// HTTP method helpers
export const httpMethods = {
  GET: (url: string, options?: RequestInit) => fetch(url, { method: 'GET', ...options }),
  POST: (url: string, body?: any, options?: RequestInit) => fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: body ? JSON.stringify(body) : undefined,
    ...options,
  }),
  PUT: (url: string, body?: any, options?: RequestInit) => fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: body ? JSON.stringify(body) : undefined,
    ...options,
  }),
  PATCH: (url: string, body?: any, options?: RequestInit) => fetch(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: body ? JSON.stringify(body) : undefined,
    ...options,
  }),
  DELETE: (url: string, options?: RequestInit) => fetch(url, { method: 'DELETE', ...options }),
};

// Generic API call wrapper
export const apiCall = async <T = any>(
  method: keyof typeof httpMethods,
  url: string,
  body?: any,
  options?: RequestInit & { mapper?: (data: any, mapperInstance: typeof backendMapper) => any }
): Promise<T> => {
  // GET y DELETE no llevan body: si se les pasa, el segundo argumento son las options.
  const response = await (method === 'GET' || method === 'DELETE'
    ? httpMethods[method](url, options)
    : httpMethods[method](url, body, options));
  
  if (!response.ok) {
    throw new ApiError(
      `HTTP ${response.status}: ${response.statusText}`,
      `HTTP_${response.status}`,
      response.status
    );
  }
  
  const data = await response.json();
  return apiRequest(() => Promise.resolve(data), options?.mapper);
};

const apiService = {
  API_ENDPOINTS,
  apiRequest,
  apiCall,
  httpMethods,
  ApiError,
};

export default apiService;
